import UserHasWallet from "@/onboarding/user-is-ready"
import FullscreenForm from "@/components/layout/fullscreen-form"
import { Button } from "@/components/ui/button"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { invoiceSchema } from "./invoice.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form } from "@/components/ui/form"
import InvoiceForm from "./invoice.form"
import { InvoiceStatus, useInvoicesActions } from "@/api/invoices"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import InvoicePreview from "./invoice-preview"
import InvoiceSendModal from "./invoice-send.modal"
import { DesktopOnly } from "@/components/ux/desktop-only"

interface InvoiceEditProps {
  id?: string;
  invoice: Partial<z.input<typeof invoiceSchema>>;
}

export default function InvoiceEdit({ invoice, id }: InvoiceEditProps) {
  const { create, update } = useInvoicesActions()
  const [isCreating, setIsCreating] = useState(false)
  const navigate = useNavigate()
  const form = useForm<
    z.input<typeof invoiceSchema>,
    undefined,
    z.output<typeof invoiceSchema>
  >({
    resolver: zodResolver(invoiceSchema),
    defaultValues: invoice,
  })
  const [openSendModal, setOpenSendModal] = useState(false)

  const updateOrCreate = async (data: z.output<typeof invoiceSchema>) => {
    if (id) {
      return update(id, data)
    } else {
      return create(data)
    }
  }

  const onSubmit = async (data: z.output<typeof invoiceSchema>) => {
    setIsCreating(true)
    data.status = InvoiceStatus.PENDING
    await updateOrCreate(data)
    setIsCreating(false)
    navigate(`/invoices`)
  }

  const onSaveDraft = async (data: z.infer<typeof invoiceSchema>) => {
    setIsCreating(true)
    await updateOrCreate(data)
    setIsCreating(false)
    navigate(`/invoices`)
    toast.success("Invoice saved as draft")
  }

  return (
    <UserHasWallet>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} id="invoice">
          <FullscreenForm
            backTo="/invoices"
            buttons={[
              <Button
                key="draft"
                variant="outline"
                disabled={isCreating}
                type="button"
                onClick={form.handleSubmit(onSaveDraft)}
              >
                Save Draft
              </Button>,
              <Button
                key="create"
                variant="default"
                disabled={isCreating}
                type="button"
                onClick={async () => setOpenSendModal(await form.trigger())}
                data-testid="send-invoice"
              >
                Send
              </Button>,
            ]}
          >
            <InvoiceSendModal
              open={openSendModal}
              onOpenChange={setOpenSendModal}
              invoice={form.getValues()}
            />
            <div className="flex flex-grow flex-col md:flex-row min-h-0">
              <div className="p-12 flex-grow overflow-scroll">
                <div className="text-lg font-semibold leading-none tracking-tight pb-4">
                  Create Invoice
                </div>
                <InvoiceForm />
              </div>
              <DesktopOnly>
                <InvoicePreview invoice={form.watch()} />
              </DesktopOnly>
            </div>
          </FullscreenForm>
        </form>
      </Form>
    </UserHasWallet>
  )
}
